import { formatPersonName } from "@/lib/format";
import type { EventEntity, EventItem } from "@/lib/lecturehub/types";

/**
 * Event-recording helpers — the event analogue of `lessonNav.ts`, kept
 * deliberately separate from the course curriculum code. An event is a flat
 * playlist of items (`event.items`); unlike a course it has no sections, no
 * per-lesson free/locked split, and no progress model — once you're enrolled
 * you may watch every recording freely.
 */

/** Items in display order (by `sortkey`, stable for equal/absent keys). */
export function orderedItems(ev: EventEntity | null | undefined): EventItem[] {
  const items = ev?.items ?? [];
  return items
    .map((item, i) => ({ item, i }))
    .sort((a, b) => {
      const sa = a.item.sortkey ?? a.i;
      const sb = b.item.sortkey ?? b.i;
      return sa - sb || a.i - b.i;
    })
    .map(({ item }) => item);
}

/** A lecture item carrying a video is the only thing that's actually playable. */
export function isRecording(it: EventItem): boolean {
  return it.refType === "lecture" && !!it.lecture?.video;
}

/** First playable recording's id — the target for the "Watch recordings" CTA. */
export function firstRecordingId(ev: EventEntity | null | undefined): string | null {
  return orderedItems(ev).find(isRecording)?.id ?? null;
}

export function recordingTitle(it: EventItem): string {
  if (it.refType === "lecture") return it.lecture?.name ?? "Lecture";
  return it.quiz?.name ?? "Quiz";
}

export function recordingSubtitle(it: EventItem): string | null {
  if (it.refType !== "lecture" || !it.lecture?.instructors?.length) return null;
  return it.lecture.instructors
    .slice(0, 2)
    .map((ins) => formatPersonName(ins))
    .filter(Boolean)
    .join(" · ");
}

export function recordingThumb(it: EventItem): { uri: string; alt: string } | null {
  if (it.refType === "lecture") {
    const ins = it.lecture?.instructors?.[0];
    const uri = ins?.thumb?.uri ?? ins?.image?.uri;
    if (!uri) return null;
    return {
      uri,
      alt: (ins ? formatPersonName(ins) : "") || (it.lecture?.name ?? ""),
    };
  }
  const uri = it.quiz?.image?.uri;
  return uri ? { uri, alt: it.quiz?.name ?? "" } : null;
}
